import { isNil } from "lodash-es";

import { ChatDto, ChatParticipantDto, GeneralScopeDto } from "@/core/api/generated/v1";
import { AppRootState } from "@/store";

import {
  ChatsState,
  OpenChatInfo,
  getChatStoreKey,
  getChatStoreKeyById,
  getChatStoreKeyByScope,
  isOpenChatInfoEqual,
} from "./chatsSlice";

export const selectIsChatOpening = (state: AppRootState, chatId?: string | null): boolean => {
  return (
    (chatId &&
      state.communication.chats.loading.openChat &&
      state.communication.chats.loading.openChat[chatId]) ||
    false
  );
};

export const selectIsChatClosing = (state: AppRootState, chatId?: string | null): boolean => {
  return (
    (chatId &&
      state.communication.chats.loading.closeChat &&
      state.communication.chats.loading.closeChat[chatId]) ||
    false
  );
};

export const selectChat = (
  state: AppRootState,
  params: { chat?: ChatDto | null; chatId?: string | null; scope?: GeneralScopeDto | null },
) => {
  return (
    state.communication.chats.chatMap[getChatStoreKey(params.chat)] ||
    state.communication.chats.chatMap[getChatStoreKeyById(params.chatId)] ||
    state.communication.chats.chatMap[getChatStoreKeyByScope(params.scope)] ||
    undefined
  );
};

export const selectOpenedChatWithAnyPlacement = (
  state: AppRootState,
  openedChat?: OpenChatInfo | null,
): OpenChatInfo | undefined => {
  return (
    state.communication.chats.openedChats?.find((x) =>
      isOpenChatInfoEqual(x, openedChat, { comparePlacement: false }),
    ) || undefined
  );
};

export const selectOpenedChatsWithAnyPlacement = (
  state: AppRootState,
  openedChat?: OpenChatInfo | null,
): OpenChatInfo[] => {
  return (
    state.communication.chats.openedChats?.filter((x) =>
      isOpenChatInfoEqual(x, openedChat, { comparePlacement: false }),
    ) || []
  );
};

export const selectOpenedChatInChatsState = (
  state: ChatsState,
  openedChat?: OpenChatInfo | null,
): OpenChatInfo | undefined => {
  return state.openedChats?.find((x) => isOpenChatInfoEqual(x, openedChat)) || undefined;
};

export const selectOpenedChat = (
  state: AppRootState,
  openedChat?: OpenChatInfo | null,
): OpenChatInfo | undefined => {
  return selectOpenedChatInChatsState(state.communication.chats, openedChat);
};

export const selectChatsWithMeInParticipants = (state: AppRootState) => {
  const profile = state.profile.profile;
  const chats = Object.values(state.communication.chats.chatMap).filter((x) => !!x) as ChatDto[];

  return chats.filter((chat) => {
    const participants =
      state.communication.chatParticipants.paginatedChatParticipantMap[chat?.id || ""]?.items || [];
    return participants.some((p) => p.userId === profile?.id);
  });
};

export const selectChatCurrentParticipant = (
  state: AppRootState,
  chatId: Nil<string>,
  tenantId?: string | null,
  userId?: string | null,
) => {
  const tenantIdComputed = tenantId || state.tenantProfile.profile?.id;
  const userIdComputed = userId || state.profile.profile?.id;
  const chatParticipants =
    state.communication.chatParticipants.paginatedChatParticipantMap[chatId || ""]?.items;
  const currentParticipant = chatParticipants?.find(
    (x) =>
      (isNil(tenantIdComputed) ? isNil(x.tenantId) : x.tenantId == tenantIdComputed) &&
      x.userId === userIdComputed,
  );
  return currentParticipant;
};

/** Returns all participants for currently authenticated user. */
export const selectCurrentUserChatParticipants = (state: AppRootState) => {
  const profile = state.profile.profile;
  const participants: ChatParticipantDto[] = [];

  const paginatedChatParticipantMap =
    state.communication.chatParticipants.paginatedChatParticipantMap || {};
  for (const chatId in paginatedChatParticipantMap) {
    const participant = paginatedChatParticipantMap[chatId]?.items?.find(
      (x) => x.userId === profile?.id,
    );
    if (participant) {
      participants.push(participant);
    }
  }
  return participants;
};

export const selectChatParticipantStatus = (
  state: AppRootState,
  chatId?: string | null,
  participantId?: string | null,
) => {
  const participantStatus =
    (state.communication.chatParticipants.participantStatusMap[chatId || ""] || {})[
      participantId || ""
    ] || {};
  return participantStatus;
};

export const selectChatParticipantOnlineStatus = (
  state: AppRootState,
  chatId?: string | null,
  participantId?: string | null,
) => {
  const participantStatus =
    (state.communication.chatParticipants.participantStatusMap[chatId || ""] || {})[
      participantId || ""
    ] || {};
  return participantStatus?.onlineStatus;
};

export const selectChatCurrentNegotiations = (state: AppRootState, chatId?: string | null) => {
  return state.communication.negotiations.currentNegotiationsMap[chatId || ""] || undefined;
};
